import { supabase } from '@/integrations/supabase/client';
import type {
  BrandLogoStoragePort,
  UploadLogoResult,
} from '../ports/brandLogoStorage.port';

const BRAND_LOGOS_BUCKET = 'brand-logos';

const getFileExtension = (file: File): string => {
  const extension = file.name.split('.').pop()?.toLowerCase();
  if (extension) return extension;

  switch (file.type) {
    case 'image/png':
      return 'png';
    case 'image/webp':
      return 'webp';
    case 'image/gif':
      return 'gif';
    default:
      return 'jpg';
  }
};

export class SupabaseBrandLogoStorage implements BrandLogoStoragePort {
  async uploadLogo(orgId: string, brandName: string, file: File): Promise<UploadLogoResult> {
    const extension = getFileExtension(file);
    const sanitizedName = brandName
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    const filePath = `${orgId}/${sanitizedName || 'brand'}-${crypto.randomUUID()}.${extension}`;

    const { data: uploadData, error: uploadError } = await supabase.storage
      .from(BRAND_LOGOS_BUCKET)
      .upload(filePath, file, {
        contentType: file.type || 'image/jpeg',
        upsert: false,
      });

    if (uploadError) throw uploadError;

    const { data: publicUrlData } = supabase.storage
      .from(BRAND_LOGOS_BUCKET)
      .getPublicUrl(uploadData.path);

    return {
      path: uploadData.path,
      publicUrl: publicUrlData.publicUrl,
    };
  }

  async deleteLogo(path: string): Promise<void> {
    const { error } = await supabase.storage
      .from(BRAND_LOGOS_BUCKET)
      .remove([path]);

    if (error) {
      console.error('Error deleting logo from storage:', error);
      throw error;
    }
  }
}

export const supabaseBrandLogoStorage = new SupabaseBrandLogoStorage();
